import { useState } from 'react';
import { STATUSES, statusVar } from '../plan/arc';
import type { Arc, Status } from '../plan/arc';
import type { Registries } from '../plan/registries';
import type { Theme } from '../design/tokens';
import { useBoardDrag } from '../plan/useBoardDrag';
import type { BoardDrag, DropTarget } from '../plan/useBoardDrag';
import { Wall } from './Shelves';
import { StatusGlyph } from './StatusGlyph';
import { StatusPicker } from './StatusPicker';
import { tintOf } from './tint';

/**
 * One storyline's members as status columns, for deciding what happens next
 * rather than for reading what the storyline says.
 *
 * A storyline holding a dozen arcs lists them in a rail that says nothing
 * about shape — you can see WHAT it holds and not what state the work is in,
 * which is the question you open a big storyline to answer. The columns carry
 * that, and a member changes state by being dragged into one.
 *
 * Everything inside a column is the board's: [Wall] lays the cards out and
 * `ArcCard` draws them, so a member here is the same object as the same arc on
 * the desk, with the same tint, the same status button and the same members
 * listed inside it. [useBoardDrag] reads `[data-status]` off the column and
 * `[data-arc-name]` off the card, which is why an empty column is a drop
 * target without anything here arranging for it.
 *
 * Scope is deliberately the DIRECT members: a held storyline is one card that
 * opens into its own lanes, which keeps depth from arriving as an indentation
 * ladder nobody asked for.
 */
export function ArcLanes({
  members,
  registries,
  theme,
  onMove,
  onSetStatus,
  onSetArchived,
}: {
  members: Arc[];
  registries: Registries;
  theme: Theme;
  /** Given a member name and where it landed, write the move. */
  onMove: (name: string, target: DropTarget) => void;
  /** The status button on a card, which is the same write without the drag. */
  onSetStatus: (arc: Arc, status: Status, evidence?: string) => void;
  onSetArchived?: (arc: Arc, archived: boolean) => void;
}) {
  const drag = useBoardDrag(onMove);
  const [picking, setPicking] = useState<{ arc: Arc; anchor: DOMRect | null } | null>(null);
  const carried = members.find((m) => m.name === drag.draggingName) ?? null;

  return (
    <div
      className={`arc-lanes${drag.draggingName ? ' is-dragging' : ''}`}
      onPointerMove={drag.onBoardPointerMove}
      onPointerUp={drag.onBoardPointerUp}
      onPointerCancel={drag.onBoardPointerUp}
    >
      {STATUSES.map((status) => (
        <LaneColumn
          key={status}
          status={status}
          // The board's order, so a member sits where the desk would put it
          // rather than in whatever order the holder happens to list.
          members={members
            .filter((m) => m.status === status)
            .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))}
          registries={registries}
          theme={theme}
          drag={drag}
          // Lit while the pointer is over it, so the answer to "where is this
          // going to land" is on the column rather than inferred from a gap.
          isTarget={drag.draggingName !== null && drag.dropTarget?.status === status}
          onPick={(arc, anchor) => setPicking({ arc, anchor: anchor ?? null })}
        />
      ))}

      {carried && drag.pointer && (
        // Follows the pointer as an outline rather than a clone: the real card
        // stays in place, dimmed, so the column it came from does not reflow
        // under the cursor while you are still deciding.
        <div
          className="arc-lane-ghost"
          style={{ left: drag.pointer.x + 14, top: drag.pointer.y + 12 }}
          aria-hidden="true"
        >
          <StatusGlyph status={carried.status} />
          <span className="arc-lane-ghost-title">{carried.title}</span>
        </div>
      )}

      {picking && (
        <StatusPicker
          arc={picking.arc}
          anchor={picking.anchor}
          onPick={(status, evidence) => {
            onSetStatus(picking.arc, status, evidence);
            setPicking(null);
          }}
          onArchive={
            onSetArchived &&
            ((archived) => {
              onSetArchived(picking.arc, archived);
              setPicking(null);
            })
          }
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}

function LaneColumn({
  status,
  members,
  registries,
  theme,
  drag,
  isTarget,
  onPick,
}: {
  status: Status;
  members: Arc[];
  registries: Registries;
  theme: Theme;
  drag: BoardDrag;
  isTarget: boolean;
  onPick: (arc: Arc, anchor?: DOMRect) => void;
}) {
  return (
    // Every status gets a column, including the empty ones: a lane you cannot
    // drop into because nothing is in it yet is the one you most want on a
    // first pass over a storyline nobody has triaged.
    <section
      className={`arc-lane${isTarget ? ' is-target' : ''}`}
      data-status={status}
      style={{ '--lane': `var(${statusVar(status)})` } as React.CSSProperties}
    >
      <div className="arc-lane-head">
        <StatusGlyph status={status} />
        <span className="t-eyebrowMono" style={{ color: `var(${statusVar(status)})` }}>
          {status}
        </span>
        <span className="t-metaMono band-count">{members.length}</span>
      </div>

      <div className="arc-lane-body">
        <Wall
          // One card per member — a member's own members ride inside its card,
          // which is what the card is for, rather than as rows beside it.
          groups={members.map((arc) => ({ holder: { arc, depth: 0 }, members: [] }))}
          registries={registries}
          theme={theme}
          hold="tint"
          tintOf={tintOf}
          drag={drag}
          onPick={onPick}
        />

        {members.length === 0 && (
          <p className="t-metaMono arc-lane-empty">
            {drag.draggingName ? 'drop here' : 'nothing'}
          </p>
        )}
      </div>
    </section>
  );
}
